import { useTranslation } from "react-i18next";

import type { ExamSpec } from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamIntroCard
 *
 * Pre-start panel shown on /exam/:stateCode. Renders the state banner +
 * the spec summary (Q count, time limit converted to minutes, pass %)
 * and a Start button that the page wires to POST /api/exam/start.
 *
 * Bank-insufficient mode: when the backend reports bankSufficient=false
 * we hide the Start button entirely and surface a "coming soon" empty
 * state instead — the state has a DMV spec but we don't have enough
 * practice questions to run a full exam yet.
 *
 * No interactive state lives here; all network calls happen in ExamPage.
 */
export interface ExamIntroCardProps {
  stateCode: string;
  spec: ExamSpec;
  onStart: () => void;
  bankInsufficient?: boolean;
  bankAvailable?: number;
}

function minutesFrom(seconds: number): number {
  return Math.max(1, Math.round(seconds / 60));
}

export default function ExamIntroCard({
  stateCode,
  spec,
  onStart,
  bankInsufficient = false,
  bankAvailable,
}: ExamIntroCardProps) {
  const { t } = useTranslation();
  const minutes = minutesFrom(spec.timeLimitSeconds);

  return (
    <section
      data-testid="exam-intro-card"
      aria-labelledby="exam-intro-heading"
      className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <header className="flex items-center justify-between gap-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {t("exam.intro.eyebrow")}
          </p>
          <h1
            id="exam-intro-heading"
            className="mt-xs font-display text-2xl font-bold text-brand-primary sm:text-3xl"
          >
            {t("exam.intro.title")}
          </h1>
        </div>
        <span
          data-testid="exam-intro-state"
          className="rounded-full bg-brand-primary/10 px-sm py-xs text-sm font-semibold uppercase tracking-wide text-brand-primary"
        >
          {stateCode}
        </span>
      </header>

      <p className="mt-md text-base text-neutral-800">
        {t("exam.intro.subtitle", { state: stateCode })}
      </p>

      <dl className="mt-lg grid grid-cols-1 gap-md sm:grid-cols-3">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {t("exam.intro.questionsLabel")}
          </dt>
          <dd
            data-testid="exam-intro-q-count"
            className="mt-xs text-2xl font-bold text-brand-primary"
          >
            {spec.questionCount}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {t("exam.intro.timeLabel")}
          </dt>
          <dd
            data-testid="exam-intro-time"
            className="mt-xs text-2xl font-bold text-brand-primary"
          >
            {t("exam.intro.timeValue", { minutes })}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {t("exam.intro.passLabel")}
          </dt>
          <dd
            data-testid="exam-intro-pass-percent"
            className="mt-xs text-2xl font-bold text-brand-primary"
          >
            {t("exam.intro.passValue", { percent: spec.passPercent })}
          </dd>
        </div>
      </dl>

      {bankInsufficient ? (
        <div
          data-testid="exam-intro-bank-insufficient"
          role="status"
          aria-live="polite"
          className="mt-lg rounded-md border border-brand-primary/20 bg-brand-surface/40 p-md text-sm text-neutral-800"
        >
          {t("exam.intro.bankInsufficient", {
            state: stateCode,
            available: bankAvailable ?? 0,
            required: spec.questionCount,
          })}
        </div>
      ) : (
        <div className="mt-lg">
          <button
            type="button"
            data-testid="exam-intro-start"
            onClick={onStart}
            className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-base font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("exam.intro.startButton")}
          </button>
        </div>
      )}
    </section>
  );
}
